import { useState, useEffect } from "react";
import { AlertTriangle, Check, X, Loader2, Eye, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface Props {
  logAction: (action: string, entityType: string, entityId: string, details?: any) => Promise<void>;
  currentUserId: string | null;
}

interface FlaggedRestaurant {
  id: string;
  restaurant_id: string;
  restaurant_name: string;
  slug: string;
  owner_id: string;
  reason: string;
  status: string;
  reviewed_by: string | null;
  reviewed_at: string | null;
  review_notes: string | null;
  created_at: string;
}

const AdminReviewTab = ({ logAction, currentUserId }: Props) => {
  const { toast } = useToast();
  const [flagged, setFlagged] = useState<FlaggedRestaurant[]>([]);
  const [loading, setLoading] = useState(true);
  const [reviewNotes, setReviewNotes] = useState<Record<string, string>>({});
  const [processing, setProcessing] = useState<string | null>(null);
  const [filter, setFilter] = useState<"pending" | "all">("pending");

  useEffect(() => {
    loadFlagged();
  }, [filter]);

  const loadFlagged = async () => {
    setLoading(true);
    let query = supabase
      .from("flagged_restaurants")
      .select("*")
      .order("created_at", { ascending: false });

    if (filter === "pending") {
      query = query.eq("status", "pending");
    }

    const { data, error } = await query;
    if (error) {
      toast({ title: "Erro ao carregar", description: error.message, variant: "destructive" });
    }
    setFlagged((data as FlaggedRestaurant[]) || []);
    setLoading(false);
  };

  const handleReview = async (item: FlaggedRestaurant, action: "approved" | "rejected") => {
    setProcessing(item.id);
    try {
      // Update flag status
      const { error: flagError } = await supabase
        .from("flagged_restaurants")
        .update({
          status: action,
          reviewed_by: currentUserId,
          reviewed_at: new Date().toISOString(),
          review_notes: reviewNotes[item.id] || null,
        })
        .eq("id", item.id);

      if (flagError) throw flagError;

      if (action === "rejected") {
        // Deactivate the restaurant
        await supabase
          .from("restaurants")
          .update({ is_active: false })
          .eq("id", item.restaurant_id);
      }

      await logAction(
        `review_${action}`,
        "flagged_restaurant",
        item.id,
        { restaurant_id: item.restaurant_id, restaurant_name: item.restaurant_name, slug: item.slug }
      );

      setFlagged((prev) => prev.map((f) => f.id === item.id ? { ...f, status: action } : f));
      toast({
        title: action === "approved" ? "Restaurante aprovado ✅" : "Restaurante rejeitado ❌",
        description: `"${item.restaurant_name}" foi ${action === "approved" ? "aprovado" : "rejeitado"}.`,
      });
    } catch (err: any) {
      toast({ title: "Erro", description: err.message, variant: "destructive" });
    } finally {
      setProcessing(null);
    }
  };

  const pendingCount = flagged.filter((f) => f.status === "pending").length;

  if (loading) {
    return (
      <div className="flex justify-center p-12">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display font-semibold text-lg flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Revisão de Nomes Sinalizados
          </h2>
          <p className="text-xs text-muted-foreground">
            Restaurantes sinalizados pelo filtro de conteúdo impróprio
          </p>
        </div>
        {pendingCount > 0 && (
          <Badge className="bg-amber-500 text-white">
            {pendingCount} pendente{pendingCount > 1 ? "s" : ""}
          </Badge>
        )}
      </div>

      <div className="flex gap-2">
        <Button
          size="sm"
          variant={filter === "pending" ? "default" : "outline"}
          onClick={() => setFilter("pending")}
          className="text-xs"
        >
          Pendentes
        </Button>
        <Button
          size="sm"
          variant={filter === "all" ? "default" : "outline"}
          onClick={() => setFilter("all")}
          className="text-xs"
        >
          Todos
        </Button>
      </div>

      {flagged.length === 0 ? (
        <div className="rounded-xl border border-border bg-card p-12 text-center">
          <Check className="h-10 w-10 mx-auto mb-3 text-emerald-500 opacity-50" />
          <p className="text-sm text-muted-foreground">
            {filter === "pending"
              ? "Nenhum restaurante pendente de revisão"
              : "Nenhum restaurante sinalizado"}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {flagged.map((item) => (
            <div
              key={item.id}
              className="rounded-xl border border-border bg-card p-4 space-y-3"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-sm">{item.restaurant_name}</h3>
                    <Badge
                      variant={
                        item.status === "pending"
                          ? "default"
                          : item.status === "approved"
                          ? "secondary"
                          : "destructive"
                      }
                      className={`text-[10px] ${
                        item.status === "pending"
                          ? "bg-amber-500 text-white"
                          : item.status === "approved"
                          ? "bg-emerald-500 text-white"
                          : ""
                      }`}
                    >
                      {item.status === "pending"
                        ? "Pendente"
                        : item.status === "approved"
                        ? "Aprovado"
                        : "Rejeitado"}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <ExternalLink className="h-3 w-3" />
                      /{item.slug}
                    </span>
                    <span>
                      {new Date(item.created_at).toLocaleDateString("pt-BR", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                  </div>
                  <p className="text-xs text-amber-600 mt-1">
                    Motivo: {item.reason === "profanity_detected" ? "Conteúdo impróprio detectado" : item.reason}
                  </p>
                </div>
              </div>

              {item.status === "pending" && (
                <div className="space-y-2">
                  <Textarea
                    placeholder="Notas da revisão (opcional)..."
                    value={reviewNotes[item.id] || ""}
                    onChange={(e) =>
                      setReviewNotes((prev) => ({ ...prev, [item.id]: e.target.value }))
                    }
                    className="text-sm h-16 resize-none bg-secondary/50"
                  />
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => handleReview(item, "approved")}
                      disabled={processing === item.id}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
                    >
                      {processing === item.id ? (
                        <Loader2 className="h-3 w-3 animate-spin mr-1" />
                      ) : (
                        <Check className="h-3 w-3 mr-1" />
                      )}
                      Aprovar
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleReview(item, "rejected")}
                      disabled={processing === item.id}
                      className="text-xs"
                    >
                      {processing === item.id ? (
                        <Loader2 className="h-3 w-3 animate-spin mr-1" />
                      ) : (
                        <X className="h-3 w-3 mr-1" />
                      )}
                      Rejeitar
                    </Button>
                  </div>
                </div>
              )}

              {item.review_notes && item.status !== "pending" && (
                <p className="text-xs text-muted-foreground italic">
                  Notas: {item.review_notes}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminReviewTab;
